<template>
  <v-dialog v-model="dialogVisible" max-width="1200px" scrollable persistent>
    <v-card class="beneficiary-calendar-modal">
      <!-- 헤더 -->
      <div class="modal-header">
        <div class="header-left">
          <v-icon size="24" color="white">mdi-calendar-account</v-icon>
          <div class="header-title-section">
            <div class="beneficiary-navigator">
              <button 
                class="nav-btn-circle" 
                @click="navigateBeneficiary(-1)"
                :disabled="!canNavigatePrev"
                title="이전 수급자"
              >
                <v-icon size="18">mdi-chevron-left</v-icon>
              </button>
              <div class="beneficiary-selector-wrapper">
                <select v-model="currentBeneficiaryIndex" class="beneficiary-select" @change="onBeneficiaryChange">
                  <option 
                    v-for="(ben, idx) in beneficiaryList" 
                    :key="ben.patient_code" 
                    :value="idx"
                  >
                    {{ ben.patient_name }}
                  </option>
                </select>
                <span class="beneficiary-counter">{{ currentBeneficiaryIndex + 1 }} / {{ beneficiaryList.length }}</span>
              </div>
              <button 
                class="nav-btn-circle" 
                @click="navigateBeneficiary(1)"
                :disabled="!canNavigateNext"
                title="다음 수급자"
              >
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>
        </div>
        <div class="header-controls">
          <div class="year-month-selector">
            <button class="nav-btn" @click="changeMonth(-1)">
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <div class="current-period">
              <select v-model="selectedYear" class="year-select">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}년</option>
              </select>
              <select v-model="selectedMonth" class="month-select">
                <option v-for="m in 12" :key="m" :value="m">{{ m }}월</option>
              </select>
            </div>
            <button class="nav-btn" @click="changeMonth(1)">
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>
          <button class="pdf-btn" @click="printSchedulePdf" :disabled="isPdfLoading || scheduleData.length === 0" title="일정표 PDF 출력">
            <v-icon size="18" color="white">{{ isPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
            <span>PDF</span>
          </button>
          <button class="close-btn" @click="close">
            <v-icon size="20" color="white">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- 컨텐츠 -->
      <div class="modal-content">
        <div v-if="isLoading" class="loading-state">
          <v-progress-circular indeterminate color="#0d47a1" size="40" />
          <span>일정을 불러오는 중...</span>
        </div>

        <div v-else-if="scheduleData.length === 0" class="empty-state">
          <v-icon size="56" color="#cbd5e1">mdi-calendar-blank</v-icon>
          <h3>일정 데이터가 없습니다</h3>
          <p>{{ selectedYear }}년 {{ selectedMonth }}월에 {{ currentBeneficiary?.patient_name }}님의 일정이 없습니다.</p>
          <LtcsMissingRequestCta
            :member-code="memberCode"
            :biz-gubun="bizGubun"
            :year-month="requestYearMonth"
            :types="SCHEDULE_LTCS_REQUEST_TYPES"
            @updated="fetchScheduleData"
          />
        </div>

        <template v-else>
          <div class="calendar-grid-container">
            <!-- 수급자 정보 바 -->
            <div class="beneficiary-info-bar">
              <div class="beneficiary-badge">
                <v-avatar size="40" color="#1e3a5f">
                  <span class="avatar-text">{{ currentBeneficiary?.patient_name?.charAt(0) }}</span>
                </v-avatar>
                <div class="badge-info">
                  <span class="badge-name">{{ currentBeneficiary?.patient_name }}</span>
                  <span class="badge-code">{{ maskCode(currentBeneficiary?.patient_code) }}</span>
                </div>
              </div>
              <div class="quick-stats">
                <div class="stat">
                  <span class="stat-value">{{ monthlyStats.totalDays }}</span>
                  <span class="stat-label">서비스일</span>
                </div>
                <div class="stat">
                  <span class="stat-value">{{ monthlyStats.totalHours }}</span>
                  <span class="stat-label">총 시간</span>
                </div>
                <div class="stat">
                  <span class="stat-value">{{ monthlyStats.caregiverCount }}</span>
                  <span class="stat-label">담당 종사자</span>
                </div>
              </div>
            </div>

            <!-- 달력 -->
            <div class="calendar-wrapper">
              <div class="calendar-header">
                <div class="weekday" v-for="day in weekDays" :key="day" :class="{ sunday: day === '일', saturday: day === '토' }">{{ day }}</div>
              </div>
              <div class="calendar-body">
                <div 
                  v-for="(day, idx) in calendarDays" 
                  :key="idx"
                  class="calendar-day"
                  :class="{ 
                    'other-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-schedule': getDaySchedules(day.date).length > 0
                  }"
                >
                  <div class="day-header">
                    <div class="day-number" :class="{ sunday: day.dayOfWeek === 0, saturday: day.dayOfWeek === 6 }">{{ day.day }}</div>
                    <span v-if="day.isToday" class="today-chip">TODAY</span>
                  </div>
                  <div v-if="day.isCurrentMonth" class="day-schedules">
                    <div 
                      v-for="(schedule, sIdx) in getDaySchedules(day.date).slice(0, 4)" 
                      :key="sIdx"
                      class="schedule-chip-inline"
                      :class="{ 'highlighted': hoveredCaregiver === schedule.clsp_caregiver_name }"
                      :style="{ borderLeftColor: getCaregiverColor(schedule.clsp_caregiver_name) }"
                      @mouseenter="hoveredCaregiver = schedule.clsp_caregiver_name"
                      @mouseleave="hoveredCaregiver = null"
                    >
                      <span class="chip-text">{{ schedule.clsp_caregiver_name }} {{ formatTime(schedule.clsp_start_time) }}-{{ formatTime(schedule.clsp_end_time) }}</span>
                    </div>
                    <div 
                      v-if="getDaySchedules(day.date).length > 4" 
                      class="more-schedules"
                    >
                      +{{ getDaySchedules(day.date).length - 4 }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 담당 종사자 목록 -->
            <div class="caregiver-list-card">
              <h4>
                <v-icon size="18" color="#1e3a5f">mdi-account-hard-hat</v-icon>
                담당 종사자 목록
              </h4>
              <div class="caregiver-chips">
                <div 
                  v-for="cg in caregiverList2" 
                  :key="cg.name"
                  class="caregiver-chip"
                  :style="{ borderLeftColor: getCaregiverColor(cg.name) }"
                  @mouseenter="hoveredCaregiver = cg.name"
                  @mouseleave="hoveredCaregiver = null"
                  :class="{ 'highlighted': hoveredCaregiver === cg.name }"
                >
                  <div class="chip-color" :style="{ backgroundColor: getCaregiverColor(cg.name) }"></div>
                  <span class="chip-name">{{ cg.name }}</span>
                  <span class="chip-count">{{ cg.count }}회</span>
                  <span class="chip-hours">{{ cg.hours }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { homeScheduleApi } from '@/services/api';
import { mapSchedulePlanRows, normalizeScheduleDate } from '@/utils/scheduleDate';
import { downloadPdfBlob, getApiErrorMessage } from '@/utils/downloadPdf';
import LtcsMissingRequestCta from '@/components/member/request/LtcsMissingRequestCta.vue';

const SCHEDULE_LTCS_REQUEST_TYPES = ['caion_ltcs_schedule_plan'] as const;

interface ScheduleData {
  clsp_idx: number;
  clsp_date: string;
  clsp_start_time: string;
  clsp_end_time: string;
  clsp_recipient_name: string;
  clsp_recipient_cert_number: string;
  clsp_caregiver_name: string;
  clsp_caregiver_birth: string;
  clsp_service_type: string;
  clsp_fee_name: string;
}

interface CalendarDay {
  day: number;
  date: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  dayOfWeek: number;
}

interface Props {
  modelValue: boolean;
  beneficiary: any;
  beneficiaries?: any[];
  memberCode: number | string;
  bizGubun?: string;
  year?: number;
  month?: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'change-beneficiary', beneficiary: any): void;
}>();

const currentBeneficiaryIndex = ref(0);
const hoveredCaregiver = ref<string | null>(null);

const beneficiaryList = computed(() => {
  if (props.beneficiaries && props.beneficiaries.length > 0) {
    return props.beneficiaries;
  }
  return props.beneficiary ? [props.beneficiary] : [];
});

const currentBeneficiary = computed(() => {
  return beneficiaryList.value[currentBeneficiaryIndex.value] || props.beneficiary;
});

const canNavigatePrev = computed(() => currentBeneficiaryIndex.value > 0);
const canNavigateNext = computed(() => currentBeneficiaryIndex.value < beneficiaryList.value.length - 1);

const navigateBeneficiary = (delta: number) => {
  const newIndex = currentBeneficiaryIndex.value + delta;
  if (newIndex >= 0 && newIndex < beneficiaryList.value.length) {
    currentBeneficiaryIndex.value = newIndex;
    emit('change-beneficiary', currentBeneficiary.value);
    fetchScheduleData();
  }
};

const onBeneficiaryChange = () => {
  emit('change-beneficiary', currentBeneficiary.value);
  fetchScheduleData();
};

const isLoading = ref(false);
const isPdfLoading = ref(false);
const scheduleData = ref<ScheduleData[]>([]);

const today = new Date();
const selectedYear = ref(props.year || today.getFullYear());
const selectedMonth = ref(props.month || (today.getMonth() + 1));
const requestYearMonth = computed(
  () => `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`
);

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
});

const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

const calendarDays = computed<CalendarDay[]>(() => {
  const year = selectedYear.value;
  const month = selectedMonth.value;

  const firstDay = new Date(year, month - 1, 1);
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - firstDay.getDay());

  const days: CalendarDay[] = [];
  const current = new Date(startDay);
  const todayStr = formatDateStr(today);

  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === month - 1;
    const dateStr = formatDateStr(current);

    days.push({
      day: current.getDate(),
      date: dateStr,
      isCurrentMonth,
      isToday: dateStr === todayStr,
      dayOfWeek: current.getDay()
    });

    current.setDate(current.getDate() + 1);
  }

  return days;
});

const monthlyStats = computed(() => {
  const uniqueDays = new Set(scheduleData.value.map(s => s.clsp_date));
  const uniqueCaregivers = new Set(scheduleData.value.map(s => s.clsp_caregiver_name));

  let totalMinutes = 0;
  scheduleData.value.forEach(s => {
    totalMinutes += calculateMinutes(s.clsp_start_time, s.clsp_end_time);
  });

  return {
    totalDays: uniqueDays.size,
    totalHours: formatHours(totalMinutes),
    caregiverCount: uniqueCaregivers.size
  };
});

const caregiverList2 = computed(() => {
  const cgData: Record<string, { count: number; minutes: number }> = {};

  scheduleData.value.forEach(s => {
    if (!cgData[s.clsp_caregiver_name]) {
      cgData[s.clsp_caregiver_name] = { count: 0, minutes: 0 };
    }
    cgData[s.clsp_caregiver_name].count++;
    cgData[s.clsp_caregiver_name].minutes += calculateMinutes(s.clsp_start_time, s.clsp_end_time);
  });

  return Object.entries(cgData)
    .map(([name, data]) => ({
      name,
      count: data.count,
      hours: formatHours(data.minutes)
    }))
    .sort((a, b) => b.count - a.count);
});

const caregiverColors: Record<string, string> = {};
const colorPalette = [
  '#3b82f6', '#ef4444', '#22c55e', '#a855f7', '#f97316',
  '#14b8a6', '#ec4899', '#4f46e5', '#eab308', '#64748b'
];

const getCaregiverColor = (name: string): string => {
  if (!caregiverColors[name]) {
    const idx = Object.keys(caregiverColors).length % colorPalette.length;
    caregiverColors[name] = colorPalette[idx];
  }
  return caregiverColors[name];
};

const formatDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTime = (time: string | undefined): string => {
  if (!time) return '';
  const str = time.toString().replace(/[^0-9]/g, '');
  if (str.length >= 4) {
    return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  }
  return time;
};

const calculateMinutes = (startTime: string, endTime: string): number => {
  const start = formatTime(startTime).split(':');
  const end = formatTime(endTime).split(':');
  if (start.length < 2 || end.length < 2) return 0;

  const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
  const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
  return Math.max(0, endMinutes - startMinutes);
};

const formatHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins > 0) return `${hours}h ${mins}m`;
  return `${hours}h`;
};

const maskCode = (code: string | undefined): string => {
  if (!code || code.length < 3) return code || '-';
  return code.slice(0, -3) + '***';
};

const getDaySchedules = (dateStr: string): ScheduleData[] => {
  return scheduleData.value.filter((s) => normalizeScheduleDate(s.clsp_date) === dateStr);
};

const changeMonth = (delta: number) => {
  let newMonth = selectedMonth.value + delta;
  let newYear = selectedYear.value;

  if (newMonth > 12) {
    newMonth = 1;
    newYear++;
  } else if (newMonth < 1) {
    newMonth = 12;
    newYear--;
  }

  selectedMonth.value = newMonth;
  selectedYear.value = newYear;
  fetchScheduleData();
};

const fetchScheduleData = async () => {
  const ben = currentBeneficiary.value;
  if (!props.memberCode || !ben?.patient_code) return;

  try {
    isLoading.value = true;
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;

    let res = await homeScheduleApi.getBeneficiarySchedule(
      props.memberCode,
      ben.patient_code,
      yearMonth,
      props.bizGubun || undefined
    );

    if (
      res.data.success &&
      (!res.data.data || res.data.data.length === 0) &&
      props.bizGubun
    ) {
      res = await homeScheduleApi.getBeneficiarySchedule(
        props.memberCode,
        ben.patient_code,
        yearMonth,
        undefined
      );
    }

    if (res.data.success) {
      scheduleData.value = mapSchedulePlanRows(res.data.data || []);
    } else {
      scheduleData.value = [];
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '일정 조회에 실패했습니다.';
    const status = error.response?.status || '';
    console.error('일정 데이터 조회 오류:', error);
    alert(`일정 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    scheduleData.value = [];
  } finally {
    isLoading.value = false;
  }
};

const printSchedulePdf = async () => {
  const ben = currentBeneficiary.value;
  if (!props.memberCode || !ben?.patient_code) return;

  try {
    isPdfLoading.value = true;
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;

    let res = await homeScheduleApi.downloadBeneficiaryPdf(
      props.memberCode,
      ben.patient_code,
      yearMonth,
      props.bizGubun || undefined
    );
    if (res.status === 404 && props.bizGubun) {
      res = await homeScheduleApi.downloadBeneficiaryPdf(
        props.memberCode,
        ben.patient_code,
        yearMonth,
        undefined
      );
    }

    downloadPdfBlob(res.data, `일정표_${ben.patient_name}_${yearMonth}.pdf`);
  } catch (error: unknown) {
    const message = await getApiErrorMessage(error, '일정표 PDF 출력에 실패했습니다.');
    console.error('PDF 출력 오류:', error);
    alert(`일정표 PDF 출력 실패: ${message}`);
  } finally {
    isPdfLoading.value = false;
  }
};

const close = () => {
  dialogVisible.value = false;
};

watch(dialogVisible, async (visible) => {
  if (visible) {
    selectedYear.value = props.year || today.getFullYear();
    selectedMonth.value = props.month || (today.getMonth() + 1);

    if (props.beneficiaries && props.beneficiaries.length > 0 && props.beneficiary) {
      const idx = props.beneficiaries.findIndex(b =>
        b.patient_code === props.beneficiary.patient_code
      );
      currentBeneficiaryIndex.value = idx >= 0 ? idx : 0;
    } else {
      currentBeneficiaryIndex.value = 0;
    }

    await nextTick();
    if (currentBeneficiary.value?.patient_code) {
      await fetchScheduleData();
    }
  }
});

watch([selectedYear, selectedMonth], async ([newYear, newMonth], [oldYear, oldMonth]) => {
  if (dialogVisible.value && (newYear !== oldYear || newMonth !== oldMonth)) {
    await fetchScheduleData();
  }
});
</script>

<style lang="scss" scoped>
.beneficiary-calendar-modal {
  overflow: hidden;
  border-radius: 8px !important;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #1e3a5f;
  color: #fff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.beneficiary-navigator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn-circle {
  width: 28px;
  height: 28px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.beneficiary-selector-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.beneficiary-select {
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  min-width: 150px;

  option {
    color: #1e293b;
    background: #fff;
  }
}

.beneficiary-counter {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.year-month-selector {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-btn {
  width: 28px;
  height: 28px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.current-period {
  display: flex;
  align-items: center;
  gap: 2px;

  .year-select,
  .month-select {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    text-align: center;

    option {
      background: #1e3a5f;
      color: #fff;
    }
  }

  .year-select {
    width: 70px;
  }

  .month-select {
    width: 50px;
  }
}

.pdf-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: #c62828;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #b71c1c;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.modal-content {
  flex: 1;
  padding: 20px;
  height: 70vh;
  min-height: 70vh;
  max-height: 70vh;
  overflow-y: auto;
  background: #fafafa;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 500px;
  gap: 12px;
  color: #64748b;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

.calendar-grid-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.beneficiary-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #1e3a5f;
  padding: 14px 20px;
  border-radius: 4px;

  .beneficiary-badge {
    display: flex;
    align-items: center;
    gap: 12px;

    .avatar-text {
      color: #fff;
      font-weight: 600;
      font-size: 16px;
    }

    .badge-info {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .badge-name {
        color: #1e293b;
        font-size: 16px;
        font-weight: 600;
      }

      .badge-code {
        color: #64748b;
        font-size: 12px;
        font-weight: 500;
      }
    }
  }

  .quick-stats {
    display: flex;
    gap: 24px;

    .stat {
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      .stat-value {
        color: #1e3a5f;
        font-size: 20px;
        font-weight: 700;
        line-height: 1;
      }

      .stat-label {
        color: #94a3b8;
        font-size: 11px;
        font-weight: 500;
        margin-top: 4px;
      }
    }
  }
}

.calendar-wrapper {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #1e3a5f;
  border-bottom: 1px solid #e2e8f0;

  .weekday {
    padding: 12px 4px;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    color: #fff;

    &.sunday {
      color: #fca5a5;
    }

    &.saturday {
      color: #93c5fd;
    }
  }
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-day {
  min-height: 90px;
  border-right: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  padding: 4px 6px;
  background: #fff;
  transition: background 0.1s;

  &:nth-child(7n) {
    border-right: none;
  }

  &:hover {
    background: #fafafa;
  }

  &.other-month {
    background: #f8f8f8;

    .day-number,
    .day-header {
      visibility: hidden;
    }

    .day-schedules {
      display: none;
    }
  }

  &.today {
    background: #fffbeb;
    border: 2px solid #f59e0b;

    .day-number {
      color: #d97706;
      font-weight: 700;
    }
  }

  &.has-schedule {
    background: #fff;
    border: 1px solid #94a3b8;
  }
}

.day-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.day-number {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;

  &.sunday {
    color: #dc2626;
  }

  &.saturday {
    color: #2563eb;
  }
}

.today-chip {
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  background: #f59e0b;
  padding: 1px 4px;
  border-radius: 2px;
}

.day-schedules {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.schedule-chip-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 2px;
  background: #fff;
  border-left: 4px solid;
  cursor: pointer;
  transition: all 0.15s;

  .chip-text {
    font-size: 10px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.highlighted {
    background: #e2e8f0;
    transform: scale(1.02);
    z-index: 1;
    position: relative;
    border-left-width: 5px;

    .chip-text {
      font-weight: 700;
    }
  }
}

.more-schedules {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  padding: 2px 4px;
  background: #f1f5f9;
  border-radius: 2px;
  text-align: center;
}

.caregiver-list-card {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  border: 1px solid #e2e8f0;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .caregiver-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .caregiver-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid;
    border-radius: 0 4px 4px 0;
    transition: all 0.15s;
    cursor: pointer;

    &:hover,
    &.highlighted {
      background: #f1f5f9;
    }

    .chip-color {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .chip-name {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
    }

    .chip-count {
      font-size: 11px;
      font-weight: 600;
      color: #1e3a5f;
      background: #e2e8f0;
      padding: 2px 8px;
      border-radius: 10px;
    }

    .chip-hours {
      font-size: 11px;
      font-weight: 500;
      color: #64748b;
    }
  }
}

@media (max-width: 900px) {
  .modal-header {
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;

    .header-controls {
      width: 100%;
      justify-content: space-between;
    }
  }

  .beneficiary-info-bar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    .quick-stats {
      width: 100%;
      justify-content: space-between;
    }
  }

  .calendar-day {
    min-height: 70px;
    padding: 3px;

    .schedule-chip-inline {
      padding: 2px 4px;

      .chip-text {
        font-size: 9px;
      }
    }
  }
}
</style>
